import * as React from 'react';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useMsal } from '@azure/msal-react';
import { useQuery } from '@tanstack/react-query';

import { getSessionData, setSessionData } from '../../config';
import { getUser } from '../../services/api';

interface Props {
  children?: React.ReactNode;
}

export const AppGuard: React.FC<Props> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const { pathname, search } = useLocation();
  const [searchParam] = useSearchParams();
  const getCredentials = searchParam.get("credential");
  const navigate = useNavigate();

  React.useEffect(() => {
    if (pathname.startsWith("/paylink")) return;
    if (pathname !== "/home") {
      getToken();
    }
  }, [instance, inProgress, navigate, pathname]);

  const getToken = async () => {
    await instance
      .handleRedirectPromise()
      .then(async () => {
        const account = instance.getActiveAccount();

        if (!account) {
          if (pathname !== "/home") {
            localStorage.setItem("redirectUrl", pathname);
          }
          //  Pick credential and save to storage before sending user to login
          savePaymentMethodCredentials();
          navigate("/login");
        } else {
          if (account && account?.idTokenClaims) {
            const redirectUrl = localStorage.getItem("redirectUrl");

            const userData = {
              conURN: account && account?.idTokenClaims.extension_UserURN,
              wRUN: account?.idTokenClaims.sub,
              name: `${account.idTokenClaims?.given_name} ${account.idTokenClaims?.family_name}`,
            };

            const existingSessionData = getSessionData();
            const updateData = existingSessionData
              ? { ...existingSessionData, ...userData }
              : userData;

            setSessionData(updateData);
            savePaymentMethodCredentials();

            if (redirectUrl) {
              navigate(redirectUrl);
              localStorage.removeItem("redirectUrl");
            }
          }
        }
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const savePaymentMethodCredentials = () => {
    if (pathname === "/payment-method") {
      const existingSessionData = getSessionData();
      if (getCredentials) {
        const updateData = {
          ...existingSessionData,
          ...JSON.parse(getCredentials),
        };
        setSessionData(updateData);
      }
    }
  };

  return accounts && accounts.length >= 1 ? (
    <React.Fragment>
      {children}
      <Outlet />
    </React.Fragment>
  ) : null;
};
